import { useEffect, useState } from 'react'

import { ApolloError } from '@apollo/client'
import {
  CreateLivestream,
  CreateLivestreamVariables,
  DeleteLivestream,
  DeleteLivestreamVariables,
  GetPbsLivestreamFeeds,
  GetPbsLivestreamFeedsVariables,
  GetStation,
  GetStationLivestreams,
  GetStationLivestreamsVariables,
  GetStationVariables,
  GetStreamSchedulesForSelection,
  GetStreamSchedulesForSelectionVariables,
  Livestream,
  UpdateLivestream,
  UpdateLivestreamVariables,
} from 'types/graphql'

import { Metadata, useMutation, useQuery } from '@cedarjs/web'
import { toast } from '@cedarjs/web/toast'

import {
  CommonArrayItem,
  LivestreamInputType,
  MetadataItemProps,
} from 'src/common/types'
import Card from 'src/components/Custom/Card/Card'
import ErrorMessage from 'src/components/Custom/ErrorMessage/ErrorMessage'
import HeadingLevel from 'src/components/Custom/Heading/Heading'
import Loader from 'src/components/Custom/Loader/Loader'
import useUserStation from 'src/lib/hooks/useUserStation'

import {
  CREATE_LIVESTREAM,
  DELETE_LIVESTREAM,
  GET_PBS_LIVESTREAM_FEEDS,
  GET_STATION_LIVESTREAMS,
  GET_STATION,
  GET_STREAM_SCHEDULES,
  UPDATE_LIVESTREAM,
} from '../queries'

import Live, { Livestreams } from './Live'

interface LivestreamManagerPageState {
  liveData: Livestreams
}

type LivestreamManagerPageProps = {
  id?: string
}

const LivestreamManagerPage = ({ id }: LivestreamManagerPageProps) => {
  const [state, setState] = useState<LivestreamManagerPageState | null>(null)
  const [invalidStation, setInvalidStation] = useState(false)

  const userStationId = useUserStation()
  const stationId = id ?? userStationId

  const [stationManagerLivestreamOptions, setStationManagerLivestreamOptions] =
    useState<MetadataItemProps[]>([])
  const [streamScheduleOptions, setStreamScheduleOptions] = useState<
    CommonArrayItem[]
  >([])
  useQuery<GetPbsLivestreamFeeds, GetPbsLivestreamFeedsVariables>(
    GET_PBS_LIVESTREAM_FEEDS,
    {
      variables: { stationId },
      onCompleted: ({ pbsLivestreamFeeds: data }) => {
        let options = []
        if (data.length > 0) {
          options = [
            { label: 'None', value: '' },
            ...data.map((f) => ({ label: f.profile, value: f.cid })),
          ]
        }
        setStationManagerLivestreamOptions(options)
      },
    }
  )
  useQuery<
    GetStreamSchedulesForSelection,
    GetStreamSchedulesForSelectionVariables
  >(GET_STREAM_SCHEDULES, {
    variables: { stationId },
    onCompleted: ({ streamSchedules }) => {
      setStreamScheduleOptions([
        { label: 'None', value: '' },
        ...streamSchedules
          .filter(
            (streamSchedule) => streamSchedule.streamMediaType === 'VIDEO'
          )
          .map((streamSchedule) => ({
            label: streamSchedule.name,
            value: streamSchedule.id,
          })),
      ])
    },
  })
  const { data: stationData, loading: loadingStation } = useQuery<
    GetStation,
    GetStationVariables
  >(GET_STATION, {
    variables: { id: stationId },
  })

  const { data: livestreamData, refetch: refetchLivestreamData } = useQuery<
    GetStationLivestreams,
    GetStationLivestreamsVariables
  >(GET_STATION_LIVESTREAMS, {
    variables: { stationId: stationId },
  })

  const [updateLivestream, { loading: loadingUpdateLivestream }] = useMutation<
    UpdateLivestream,
    UpdateLivestreamVariables
  >(UPDATE_LIVESTREAM, { onCompleted: () => refetchLivestreamData() })

  const [deleteLivestream] = useMutation<
    DeleteLivestream,
    DeleteLivestreamVariables
  >(DELETE_LIVESTREAM, {
    onCompleted: () => refetchLivestreamData(),
  })

  const [createLivestream, { loading: loadingCreateLivestream }] = useMutation<
    CreateLivestream,
    CreateLivestreamVariables
  >(CREATE_LIVESTREAM, { onCompleted: () => refetchLivestreamData() })

  const handleLivestreamUpdate = async (
    id: string,
    inputData: LivestreamInputType,
    showAlerts = true
  ) => {
    if (showAlerts) {
      await toast.promise(
        updateLivestream({
          variables: {
            id: id,
            input: inputData,
          },
        }),
        {
          loading: 'Updating livestream...',
          success: 'Livestream updated successfully',
          error: (error) => {
            if (error instanceof ApolloError) {
              return error.message
            } else {
              return 'Error updating livestream'
            }
          },
        }
      )
    } else {
      await updateLivestream({
        variables: {
          id: id,
          input: inputData,
        },
      })
    }
  }

  const handleLivestreamDelete = async (id) => {
    await toast.promise(deleteLivestream({ variables: { id: id } }), {
      loading: 'Deleting livestream...',
      success: 'Livestream deleted successfully',
      error: (error) => {
        if (error instanceof ApolloError) {
          return error.message
        } else {
          return 'Error deleting livestream'
        }
      },
    })
  }

  const handleLivestreamCreation = async (input) => {
    await toast.promise(createLivestream({ variables: { input: input } }), {
      loading: 'Creating livestream...',
      success: 'Livestream created successfully',
      error: (error) => {
        if (error instanceof ApolloError) {
          return error.message
        } else {
          return 'Error creating livestream'
        }
      },
    })
  }

  useEffect(() => {
    setInvalidStation(stationData?.station === null)
  }, [stationData])

  useEffect(() => {
    if (
      stationData &&
      stationData.station &&
      livestreamData &&
      livestreamData.stationLivestreams
    ) {
      const newState = {
        liveData: {
          channels: livestreamData.stationLivestreams.map(
            (item: Livestream, index: number) => ({
              id: item.id,
              label: `Channel ${index + 1}`,
              logo: item.logo,
              name: item.name,
              shortStationDescription: item.shortDescription,
              slug: item.slug,
              source: {
                embedCode: item.embedCode,
                pbsStationManager: item.pbsStationManagerLivestreamFeedCid,
                sourceUrl: item.sourceUrl,
                vimeoLiveEventId: item.vimeoLiveEventId,
              },
              stationDescription: item.description,
              streamScheduleId: item.streamScheduleId,
              weight: item.weight,
            })
          ),
        },
      }
      setState(newState)
    }
  }, [livestreamData, stationData])

  const stateNotNull = () => {
    return state !== null && stationData !== undefined
  }

  return (
    <div className="w-full bg-gray-100 p-4">
      <Metadata title="Livestream Settings" />
      {loadingStation ? (
        <Loader />
      ) : invalidStation ? (
        <ErrorMessage
          title="Station Not Found"
          subtitle={
            <p>
              Station with ID <code>{id}</code> not found.
            </p>
          }
        />
      ) : (
        <>
          <HeadingLevel level={1} className="mb-4">
            Livestream Settings
          </HeadingLevel>
          {stateNotNull() && id && (
            <HeadingLevel level={2}>{stationData.station?.name}</HeadingLevel>
          )}

          <div className="mt-5 space-y-6">
            {stateNotNull() && (
              <Card title="Live">
                <Live
                  data={state?.liveData}
                  onChange={(dataToUpdate) => {
                    setState((oldValue) => ({
                      ...oldValue,
                      ['liveData']: dataToUpdate,
                    }))
                  }}
                  updateLivestream={handleLivestreamUpdate}
                  createLivestream={handleLivestreamCreation}
                  deleteLivestream={handleLivestreamDelete}
                  stationId={stationId}
                  loadingCreateLivestream={loadingCreateLivestream}
                  loadingUpdateLivestream={loadingUpdateLivestream}
                  streamScheduleOptions={streamScheduleOptions}
                  stationManagerLivestreamOptions={
                    stationManagerLivestreamOptions
                  }
                />
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default LivestreamManagerPage
